import React, { useEffect, useState } from 'react';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import { InformationCircleIcon, AcademicCapIcon, PlayCircleIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import { getResourceBySlug } from '../../../lib/db';

const ManagerCommunityKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'difficulty',
      title: 'Difficulty',
      value: 'Medium - Requires team alignment and clear communication'
    },
    {
      type: 'timeline',
      title: 'Timeline',
      value: '5 Hours Prep | 1 Hour Kick-Off | 1-2 Hours per month Ongoing'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: '3-30 People Managers'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'Avoid your people leaving by ensuring a consistent and excellent people management experience'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'New/accidental/inexperienced managers, or problems being flagged around consistent manager experience, manager performance and capability'
    }
  ];

  useEffect(() => {
    getResourceBySlug('manager-community').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "Building A People Manager Community From Scratch"}
        subtitle={resource?.description || "A step-by-step guide to establishing a thriving People Manager Community of Practice (PCoP) within your startup"}
        category={resource?.category || "People & Culture"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type || ['Community', 'Management', 'Culture']}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-2" />
          Key Info
        </h2>

        {/* Quote first */}
        <div className="italic text-gray-300 text-lg p-6 border border-gray-800 rounded-lg bg-gray-900/50 mb-8">
          <p className="mb-2">
            "Help! People management is inconsistent, we have accidental people managers, inconsistent 1-2-1 PEX and i'm concerned this is a root cause for a whole host of our performance, engagement and retention challenges but I don't have a big budget to invest here. Aaarrrrrghhhhh!"
          </p>
          <p className="text-gray-400 text-sm">— All Startup People & Culture Leaders, at some point in their career.</p>
        </div>

        {/* This Playbook is for you section */}
        <h3 className="text-xl font-semibold mb-4">This Guide is for you if any of the following are true:</h3>
        <ul className="space-y-3 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>Team members aren't receiving a consistent people manager experience (messages, performance conversations, feedback conversations, expectations management)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>You have newly minted people managers, but lack a structured and effective manager onboarding experience</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>Your people managers aren't sharing best practices and helping each other to grow</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">•</span>
            <span>The role of a people manager is not well regarded within the org</span>
          </li>
        </ul>

        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4">Guide Overview</h3>
        <div className="mb-12">
          <PlaybookMetrics metrics={metrics} />
        </div>

        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed src="https://www.loom.com/embed/9d062728153645cfadce53f12da47899" title="Watch the Loom video" />
          <div className="text-center text-gray-400 text-sm mt-3">Walkthrough: How to establish a People Manager Community</div>
        </div>

        {/* What Is This Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-lg">
              Welcome to your step-by-step guide on establishing a thriving People Manager Community of Practice (PCoP) within your startup. This playbook is designed to help startup people leaders build a self-sustaining community that aligns people managers to a common principle accountability, improves their capability and reputation within the org by providing a tangible and transparent mechanism for them to demonstrate their impact.
            </p>
            <p className="text-lg">
              This playbook outlines why, when, and how to initiate a community of practice, alongside practical tools and methods for planning, designing, and facilitating impactful interactions.
            </p>
            <div className="flex items-center font-semibold mt-4 text-lg">
              Let's get into it <span className="ml-2 text-xl">🤘</span>
            </div>
          </div>
        </div>

        {/* What You'll Need Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <ul className="space-y-3 pl-5 list-disc">
            <li className="text-gray-300">3-10+ People Managers</li>
            <li className="text-gray-300">A 'Why' (See Stage 1: Recognising the need)</li>
            <li className="text-gray-300">Internal Buy-in / sponsorship from Exec Team</li>
            <li className="text-gray-300">Comms Plan e.g. 'Why are we doing this'</li>
            <li className="text-gray-300">Appropriate Tooling E.g. Miro & Slack</li>
            <li className="text-gray-300">5 Hours pre-planning co-creation time in calendar</li>
            <li className="text-gray-300">1-Hour Kick off in calendar</li>
          </ul>
        </div>

        {/* Obstacles Content */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <InformationCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Obstacles & How To Overcome Them</h3>
          </div>
          
          <div className="space-y-4">
            <ExpandableCallout
              type="warning"
              title="Initial Skepticism"
              defaultOpen={true}
            >
              <p className="text-gray-300 mb-4">
                <strong>Problem:</strong> Overcoming initial skepticism or resistance from people managers.
              </p>
              <div className="space-y-2">
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Use data from pulse/survey/team health check/retro to quantify the pain of inconsistent management support</span>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Pair with your most credible and respected people manager and lean on them to help win hearts and minds</span>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Secure executive sponsorship to demonstrate organizational commitment</span>
                </div>
              </div>
            </ExpandableCallout>

            <ExpandableCallout
              type="warning"
              title="Maintaining Engagement"
              defaultOpen={true}
            >
              <p className="text-gray-300 mb-4">
                <strong>Problem:</strong> Maintaining engagement within the new people manager community.
              </p>
              <div className="space-y-2">
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Establish a clear cadence and incorporate engaging, varied formats</span>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Crowd-source the regular roundtable topics from the community</span>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Use 'lean coffee' formats to ensure priority topics are covered</span>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Create accountability by making commitments to action public</span>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Take time to co-create a mini code of conduct & rules of engagement up front</span>
                </div>
              </div>
            </ExpandableCallout>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerCommunityKeyInfo; 